import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  IconShoppingBag,
  IconHeart,
  IconChevronRight,
  IconChevronLeft,
  IconX,
  IconZoomIn,
} from '@tabler/icons-react';
import { useProducts } from '../context/ProductsContext';
import { useSiteContent } from '../context/SiteContentContext';
import { useCart } from '../context/CartContext';
import GuyanaStripe from '../components/layout/GuyanaStripe';
import './ProductPage.css';
import PageTitle from '../components/ui/PageTitle';

export default function ProductPage() {
  var params = useParams();
  var slug = params.slug;
  var productsCtx = useProducts();
  var getProductBySlug = productsCtx.getProductBySlug;
  var loading = productsCtx.loading;
  var siteContent = useSiteContent();
  var c = siteContent.c;
  var cartCtx = useCart();
  var addItem = cartCtx.addItem;
  var _selectedColour = useState(0);
  var selectedColour = _selectedColour[0];
  var setSelectedColour = _selectedColour[1];
  var _selectedVariant = useState(0);
  var selectedVariant = _selectedVariant[0];
  var setSelectedVariant = _selectedVariant[1];
  var _showToast = useState(false);
  var showToast = _showToast[0];
  var setShowToast = _showToast[1];
  var [activeImage, setActiveImage] = useState({ slug: null, idx: 0 });
  var [zoomOpen, setZoomOpen] = useState(false);

  if (loading) {
    return <div className="pp-loading">Loading...</div>;
  }

  var product = getProductBySlug(slug);

  if (!product) {
    return (
      <div className="pp-not-found">
        <h1>Product not found</h1>
        <p>This product may have been removed or does not exist.</p>
        <Link to="/shop" className="pp-back-btn">Browse the collection</Link>
      </div>
    );
  }

  var images = (product.images || []).filter(Boolean).slice(0, 6);
  var shownImage = activeImage.slug === slug && activeImage.idx < images.length ? activeImage.idx : 0;
  var currentVariant = product.variants[selectedVariant] || product.variants[0];
  var currentColour = product.colours[selectedColour] || product.colours[0];
  var currentPrice = currentVariant ? currentVariant.price : product.base_price;
  var isLowStock = product.stock > 0 && product.stock <= product.low_stock_threshold;
  var isSoldOut = product.stock === 0;
  var storeStatus = c('store_status', 'open');

  function handleAddToCart() {
    addItem(product, currentVariant, currentColour);
    setShowToast(true);
    setTimeout(function() { setShowToast(false); }, 3000);
  }

  return (
    <>
      <PageTitle title={product.name} />
      <div className="container pp-breadcrumb">
        <Link to="/shop">Shop</Link>
        <IconChevronRight size={11} />
        <span className="pp-breadcrumb__current">{product.name}</span>
      </div>

      <div className="container pp-layout">
        <div className="pp-gallery">
          {images[shownImage] ? (
            <button
              type="button"
              className="pp-main-img pp-main-img--has-photo"
              onClick={function() { setZoomOpen(true); }}
              aria-label="See this photo full size"
            >
              <img src={images[shownImage]} alt={product.name} />
              <span className="pp-zoom-hint" aria-hidden="true"><IconZoomIn size={16} /></span>
            </button>
          ) : (
            <div className="pp-main-img"><span>Main product photo</span></div>
          )}
          {images.length > 1 && (
            <div className="pp-thumbs">
              {images.map(function(src, idx) {
                return (
                  <button
                    type="button"
                    className={'pp-thumb' + (idx === shownImage ? ' pp-thumb--active' : '')}
                    key={src + '#' + idx}
                    onClick={function() { setActiveImage({ slug: slug, idx: idx }); }}
                    aria-label={'Show photo ' + (idx + 1)}
                    aria-current={idx === shownImage ? 'true' : undefined}
                  >
                    <img src={src} alt="" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="pp-info">
          <span className="pp-category">{product.category}</span>
          <h1 className="pp-name">{product.name}</h1>
          <div className="pp-price">
            {product.compare_price && (
              <span className="pp-compare-price">{'$' + product.compare_price}</span>
            )}
            <span>{'$' + currentPrice}</span>
          </div>

          {isLowStock && <div className="pp-low-stock">Only {product.stock} left</div>}
          {isSoldOut && <div className="pp-sold-out-badge">Sold out</div>}

          <p className="pp-desc">{product.description}</p>

          {product.colours.length > 0 && (
            <div className="pp-option-group">
              <label className="pp-label">Colour</label>
              <div className="pp-colours">
                {product.colours.map(function(colour, i) {
                  return (
                    <button
                      key={colour.id}
                      className={'pp-colour-btn' + (i === selectedColour ? ' pp-colour-btn--active' : '')}
                      style={{ background: colour.hex_code }}
                      title={colour.name}
                      onClick={function() { setSelectedColour(i); }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {product.variants.length > 0 && (
            <div className="pp-option-group">
              <label className="pp-label">Size</label>
              <div className="pp-sizes">
                {product.variants.map(function(variant, i) {
                  return (
                    <button
                      key={variant.id}
                      className={'pp-size-btn' + (i === selectedVariant ? ' pp-size-btn--active' : '')}
                      onClick={function() { setSelectedVariant(i); }}
                    >
                      {variant.size_name + ' \u2014 $' + variant.price}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isSoldOut || storeStatus === 'paused' ? (
            <button className="pp-notify-btn">
              {isSoldOut ? 'Notify me when available' : 'Notify me when orders reopen'}
            </button>
          ) : (
            <button className="pp-atc" onClick={handleAddToCart}>
              <IconShoppingBag size={15} /> Add to cart
            </button>
          )}
          <button className="pp-wishlist">
            <IconHeart size={14} /> Save for later
          </button>
        </div>
      </div>

      <div className="container pp-details">
        <h2 className="pp-details-title">Craft and materials</h2>
        <div className="pp-details-grid">
          <div className="pp-detail"><strong>Materials</strong><span>{product.materials}</span></div>
          <div className="pp-detail"><strong>Dimensions</strong><span>{product.dimensions}</span></div>
          <div className="pp-detail"><strong>Made in</strong><span>Guyana, by hand</span></div>
          <div className="pp-detail"><strong>Lead time</strong><span>{c('lead_time_standard', '2\u20133 weeks') + ' per piece'}</span></div>
          <div className="pp-detail"><strong>Guarantee</strong><span>Lifetime craftsmanship warranty</span></div>
          <div className="pp-detail"><strong>Care</strong><span>Condition with natural leather balm</span></div>
        </div>
      </div>

      <GuyanaStripe />

      {zoomOpen && images.length > 0 && (
        <PhotoLightbox
          images={images}
          index={shownImage}
          name={product.name}
          onChange={function(idx) { setActiveImage({ slug: slug, idx: idx }); }}
          onClose={function() { setZoomOpen(false); }}
        />
      )}

      {showToast && (
        <div className="pp-toast">
          <span>{'\u2713 Added to your bag \u2014 ' + product.name + ', ' + (currentVariant ? currentVariant.size_name : '')}</span>
          <button onClick={function() { setShowToast(false); cartCtx.openDrawer(); }}>View bag</button>
        </div>
      )}
    </>
  );
}

// Full-size photo view: Esc or the X closes it, arrow keys or the side buttons
// step through the photos, and clicking the dark backdrop closes it too.
function PhotoLightbox(props) {
  var images = props.images;
  var index = props.index;
  var onChange = props.onChange;
  var onClose = props.onClose;
  var closeRef = useRef(null);
  var count = images.length;

  useEffect(function() {
    var opener = document.activeElement;
    var oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return function() {
      document.body.style.overflow = oldOverflow;
      if (opener && opener.focus) opener.focus();
    };
  }, []);

  useEffect(function() {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && count > 1) onChange((index + 1) % count);
      else if (e.key === 'ArrowLeft' && count > 1) onChange((index - 1 + count) % count);
    }
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [index, count, onChange, onClose]);

  return (
    <div
      className="pp-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={props.name + ' photos'}
      onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
    >
      <button type="button" ref={closeRef} className="pp-lightbox__close" onClick={onClose} aria-label="Close full-size photo">
        <IconX size={22} />
      </button>
      {count > 1 && (
        <button type="button" className="pp-lightbox__nav pp-lightbox__nav--prev" onClick={function() { onChange((index - 1 + count) % count); }} aria-label="Previous photo">
          <IconChevronLeft size={26} />
        </button>
      )}
      <img
        className="pp-lightbox__img"
        src={images[index]}
        alt={props.name + ' (photo ' + (index + 1) + ' of ' + count + ', full size)'}
      />
      {count > 1 && (
        <button type="button" className="pp-lightbox__nav pp-lightbox__nav--next" onClick={function() { onChange((index + 1) % count); }} aria-label="Next photo">
          <IconChevronRight size={26} />
        </button>
      )}
      {count > 1 && <div className="pp-lightbox__count">{(index + 1) + ' / ' + count}</div>}
    </div>
  );
}